/**
 * Rock Off Retail — band wall type-ahead filter.
 *
 * Progressive enhancement: sections/rockoff-band-wall.liquid renders a
 * fully browsable grid of links. Enter (or the submit button) always GETs
 * the real /search?q=... route via a hidden mirror field this class keeps
 * in sync — see the big comment in the Liquid file for why the visible
 * input deliberately does NOT carry name="q" itself (USF's instant-search
 * dropdown auto-binds to any input[name=q] sitewide; renaming this one
 * keeps that dropdown off the band wall, matching Sohail's "no dropdown
 * feel" ask, while the hidden field still submits the correct query).
 *
 * QC fix (12 Aug, item 4): now searches across EVERY hub in the store (the
 * `data-all-hubs` JSON blob the section emits), not just the default 12
 * tiles rendered on the wall. Live, debounced, no dropdown — matches
 * replace the grid's own tiles in place; clearing the input restores the
 * original 12 exactly (captured once, verbatim, before any filtering
 * happens).
 *
 * 50-cap fix (13 Aug): `data-all-hubs` is built server-side from
 * `metaobjects['hub'].values`, which Shopify hard-caps at the first 50
 * entries — the store has 113 hub metaobjects (84 ACTIVE) now, so that
 * blob alone silently misses everything past #50. `{% paginate %}` was
 * tested and confirmed NOT to solve this for metaobjects (see the big
 * comment in the section Liquid file). Real fix: `data-hubs-json-url` on
 * the <band-wall-filter> element points at a build-time static asset
 * (scripts/build-hubs-json.js -> assets/hubs-data.json) covering every
 * ACTIVE hub via the Admin API's genuine cursor pagination. Fetched
 * lazily — once, on first focus/keystroke, in-memory-cached after that —
 * and MERGED into the inline blob's list rather than replacing it, so a
 * failed/slow fetch (offline, CDN hiccup, asset not yet generated) never
 * breaks the existing first-50-plus-blocks behaviour; it just doesn't get
 * the extra coverage for that session.
 */
class BandWallFilter extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.status = this.querySelector('[data-band-wall-status]');
    this.grid = this.closest('.ro-band-wall')?.querySelector('[data-band-wall-grid]');
    const hubsEl = this.querySelector('[data-all-hubs]');

    if (!this.input || !this.grid) return;

    // Snapshot the merchant's real 12 (or fewer) tiles exactly as rendered,
    // so clearing the input can put the wall back to precisely this.
    this.defaultGridHTML = this.grid.innerHTML;
    this.noResultsTemplate = this.input.dataset.noResultsTemplate || '';

    this.allHubs = [];
    if (hubsEl) {
      try {
        const parsed = JSON.parse(hubsEl.textContent);
        if (Array.isArray(parsed)) {
          this.allHubs = parsed.filter((hub) => hub && typeof hub.name === 'string' && hub.name.trim() !== '');
        }
      } catch (error) {
        this.allHubs = [];
      }
    }

    // 50-cap fix: lazy, once-only, in-memory-cached fetch of the full hub
    // list. `hubsFetchPromise` doubles as the cache — a second focus/
    // keystroke before the first fetch resolves reuses the same promise
    // instead of firing a second request.
    this.hubsJsonUrl = this.dataset.hubsJsonUrl || '';
    this.hubsFetchPromise = null;

    const ensureFullHubList = () => this.fetchFullHubListOnce();

    this.debounceTimer = null;
    this.input.addEventListener('focus', ensureFullHubList, { once: true });
    this.input.addEventListener('input', () => {
      ensureFullHubList();

      // Sync the real q= field immediately (not debounced) so Enter is
      // always accurate even mid-debounce. Re-queried fresh rather than
      // cached — belt-and-braces in case USF's selector scan ever changes
      // and starts touching this field too.
      const mirror = this.querySelector('[data-band-wall-q-mirror]');
      if (mirror) mirror.value = this.input.value;

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.onInput(), 120);
    });
  }

  // Fetches assets/hubs-data.json (every ACTIVE hub, no 50-cap) at most
  // once per page load and merges it into `this.allHubs`, deduped by
  // lowercased name against whatever's already there (the inline blob's
  // first 50 hubs plus any block-only tiles). Fails silently — a missing/
  // errored/slow fetch just leaves `this.allHubs` as the inline-blob
  // fallback, never breaks the existing filter.
  fetchFullHubListOnce() {
    if (this.hubsFetchPromise) return this.hubsFetchPromise;
    if (!this.hubsJsonUrl) return Promise.resolve();

    this.hubsFetchPromise = fetch(this.hubsJsonUrl)
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('bad response'))))
      .then((data) => {
        if (!Array.isArray(data)) return;
        const seen = new Set(this.allHubs.map((hub) => hub.name.toLowerCase()));
        const additions = data.filter((hub) => hub && typeof hub.name === 'string' && hub.name.trim() !== '' && !seen.has(hub.name.toLowerCase()));
        if (additions.length) {
          this.allHubs = this.allHubs.concat(additions);
          // Refresh the currently-displayed matches if the visitor is
          // mid-search when the fetch lands, so the newly-available hubs
          // (beyond the first 50) actually show up without needing another
          // keystroke.
          if (this.input.value.trim() !== '') this.onInput();
        }
      })
      .catch(() => {
        // Offline / CDN hiccup / asset not generated yet — inline blob
        // fallback stands, nothing to log here (this is expected/handled).
      });

    return this.hubsFetchPromise;
  }

  onInput() {
    const term = this.input.value.trim().toLowerCase();

    if (term === '') {
      this.grid.innerHTML = this.defaultGridHTML;
      if (this.status) this.status.textContent = '';
      return;
    }

    const matches = this.allHubs.filter((hub) => hub.name.toLowerCase().includes(term));
    this.renderTiles(matches);

    if (!this.status) return;
    this.status.textContent = matches.length === 0 ? this.noResultsTemplate.replace('{search_term}', this.input.value.trim()) : '';
  }

  renderTiles(hubs) {
    const frag = document.createDocumentFragment();

    hubs.forEach((hub) => {
      const tile = document.createElement(hub.url ? 'a' : 'span');
      tile.className = 'ro-band-wall__tile';
      tile.setAttribute('data-band-wall-label', hub.name);
      if (hub.url) {
        tile.setAttribute('href', hub.url);
      } else {
        tile.setAttribute('role', 'link');
        tile.setAttribute('aria-disabled', 'true');
      }

      if (hub.logo) {
        const media = document.createElement('span');
        media.className = 'media media--transparent ro-band-wall__tile-media';
        const img = document.createElement('img');
        img.src = hub.logo;
        img.alt = hub.name;
        img.loading = 'lazy';
        media.appendChild(img);
        tile.appendChild(media);
      } else {
        const label = document.createElement('span');
        label.className = 'ro-band-wall__tile-label ro-stencil';
        label.textContent = hub.name;
        tile.appendChild(label);
      }

      // HUB NAME RULE (29 Sep): interest hub art is text-free, so ONLY interest
      // hubs with a logo get a live name caption; band and character logos are
      // wordmarks (the logo is the name) and get none. Strict match on purpose:
      // an entry with no/unknown type gets no caption. Mirrors the hub-index /
      // related-hubs Liquid rule (== 'interest'). Caption is absolutely
      // positioned, so it never shrinks the square art.
      if (hub.logo && hub.type === 'interest') {
        const caption = document.createElement('span');
        caption.className = 'ro-band-wall__tile-caption ro-stencil';
        caption.textContent = hub.name;
        tile.appendChild(caption);
      }
      const spotlight = document.createElement('span');
      spotlight.className = 'ro-band-wall__tile-spotlight';
      spotlight.setAttribute('aria-hidden', 'true');
      tile.appendChild(spotlight);

      frag.appendChild(tile);
    });

    this.grid.replaceChildren(frag);
  }
}

customElements.define('band-wall-filter', BandWallFilter);
